// import original module declarations
import 'styled-components';
import { DefaultTheme } from 'styled-components';
import { merge } from 'lodash';

export type ToolKey =
	| 'designElements'
	| 'printingMethods'
	| 'palette'
	| 'text'
	| 'textArt'
	| 'collages'
	| 'background'
	| 'drafts'
	| 'templates'
	| 'shapes'
	| 'uploadImage'
	| 'cliparts'
	| 'cart'
	| 'variants';
export type ToolIconKey =
	| 'design-elements'
	| 'printing-methods'
	| 'switch-variant'
	| 'text'
	| 'textart'
	| 'collages'
	| 'background'
	| 'drafts'
	| 'templates'
	| 'shapes'
	| 'upload-image'
	| 'cliparts'
	| 'cart';
// new configs
export type DesignElementsToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'designElements';
	longText?: string;
	position: number;
	text?: string;
};
export type TextToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'text';
	longText?: string;
	position: number;
	text?: string;
};
export type TextArtToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'textArt';
	longText?: string;
	position: number;
	text?: string;
};
export type ClipartToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'cliparts';
	longText?: string;
	position: number;
	text?: string;
};
export type UploadImageToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'uploadImage';
	longText?: string;
	position: number;
	text?: string;
};
export type DraftsToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'drafts';
	longText?: string;
	text?: string;
};
export type TemplatesToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'templates';
	longText?: string;
	position: number;
	text?: string;
};
export type BackgroundToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'background';
	longText?: string;
	position: number;
	text?: string;
};
export type CollagesToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'collages';
	longText?: string;
	position: number;
	text?: string;
	visible: boolean;
};
export type ShapesToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'shapes';
	longText?: string;
	position: number;
	text?: string;
	visible: boolean;
};
export type PaletteToolConfig = {
	key: 'palette';
	visible: boolean;
};
export type VariantsToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'variants';
	longText?: string;
	text?: string;
	visible: boolean;
};
export type PrintingMethodsToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'printingMethods';
	longText?: string;
	text?: string;
	visible: boolean;
};
export type CartToolConfig = {
	defaultIconKey: ToolIconKey;
	defaultLongText: string;
	defaultText: string;
	iconUrl?: string;
	key: 'cart';
	longText?: string;
	text?: string;
};

export type ToolsConfig = {
	designElements: DesignElementsToolConfig;
	uploadImage: UploadImageToolConfig;
	cliparts: ClipartToolConfig;
	text: TextToolConfig;
	textArt: TextArtToolConfig;
	background: BackgroundToolConfig;
	templates: TemplatesToolConfig;
	collages: CollagesToolConfig;
	shapes: ShapesToolConfig;
	palette: PaletteToolConfig;
	variants: VariantsToolConfig;
	printingMethods: PrintingMethodsToolConfig;
	drafts: DraftsToolConfig;
	cart: CartToolConfig;
};

export type FontFamilyConfig = {
	name: string;
	styleUrl: string;
	allTextUppercase: boolean;
};
export type FontsConfig = {
	large100bold: string;
	large200bold: string;
	large300bold: string;
	large500bold: string;
	largeBody: string;
	large200: string;
	smallBodyBold: string;
};
export type ColorsConfig = {
	primary: string;
	primaryState: string;
	accent: string;
	accentState: string;
	elementsOnPrimary: string;
	elementsOnAccent: string;
	textOnlyAccent: string;
	textMain: string;
	textSecondary: string;
	textFooter: string;
	textDisabled: string;
	backgroundMain: string;
	backgroundSecondary: string;
	backgroundCanvas: string;
	backgroundFooter: string;
	background3d: string;
	backgroundPreview: string;
	borders: string;
	iconMain: string;
	iconSecondary: string;
	iconSecondaryState: string;
	iconDisabled: string;
	error: string;
	errorText: string;
	errorLight: string;
	success: string;
	scrollAlternative: string;
};
export type SpacingConfig = {
	horizontalPadderNumber: number;
	horizontalPadderMobileNumber: number;
	horizontalPadder: string;
	horizontalPadderMobile: string;
};
export type MeasuresConfig = {
	borderRadius: string;
};
export type PanelsConfig = {
	borderWidthValue: number;
	borderBottomWidthValue: number;
	borderWidth: string;
	shadows: string;
};
export type DialogsConfig = {
	backdrop: string;
	shadows: string;
};
export type PricingConfig = {
	showPrice: boolean;
	showProductQuantity: boolean;
	priceInfo?: string;
};
export type ZoomConfig = {
	zoomPercentageIncrement: number;
};
export type Preview3DConfig = {
	allowPreview3DExpandOnContainerClick: boolean;
	showResize3DPreview: boolean;
	showResize3DHeight: number;
	showResize3DWidth: number;
};
export type GeneralConfig = {
	measurementUnit: string;
	showSideDimensions: boolean;
	showElementsDimensions: boolean;
	showSelectedElementDimensions: boolean;
	showAreaDimensions: boolean;
	isUndoRedoEnabled: boolean;
};

export type DesktopLayoutConfig = {
	footerPosition: 'top' | 'bottom';
	menuPosition: 'left' | 'right';
};
export type Theme = {
	fontFamily: FontFamilyConfig;
	fonts: FontsConfig;
	colors: ColorsConfig;
	tools: ToolsConfig;
	spacing: SpacingConfig;
	measures: MeasuresConfig;
	panels: PanelsConfig;
	dialogs: DialogsConfig;
	pricing: PricingConfig;
	zoom: ZoomConfig;
	preview3d: Preview3DConfig;
	general: GeneralConfig;
	desktopLayout: DesktopLayoutConfig;
};

// and extend them!
declare module 'styled-components' {
	export interface DefaultTheme extends Theme {}
}

const horizontalPadderNumber = 22;
const horizontalPadderMobileNumber = 20;
const panelsBorderWidth = 1;
const panelsBorderBottomWidth = 5;

export const createThemeFont = (fontFamily: string): DefaultTheme['fonts'] => {
	return {
		large100bold: `normal normal 700 14px/20px "${fontFamily}"`,
		large200bold: `normal normal 700 16px/24px "${fontFamily}"`,
		large300bold: `normal normal 700 20px/28px "${fontFamily}"`,
		large500bold: `normal normal 700 32px/40px "${fontFamily}"`,
		largeBody: `normal normal 400 14px/20px "${fontFamily}"`,
		large200: `normal normal 400 16px/24px "${fontFamily}"`,
		smallBodyBold: `normal normal 700 15px/20px "${fontFamily}"`,
	};
};

export const defaultTheme: DefaultTheme = {
	fontFamily: {
		name: 'Inter',
		styleUrl: `${process.env.PUBLIC_URL}/assets/fonts/inter/style.css`,
		allTextUppercase: false,
	},
	fonts: createThemeFont('Inter'),
	colors: {
		primary: '#0073D3',
		primaryState: '#128DF3',
		accent: '#F46200',
		accentState: '#F79F64',

		elementsOnPrimary: '#FFFFFF',
		elementsOnAccent: '#FFFFFF',
		textOnlyAccent: '#E65C00',
		textMain: '#000000',
		textSecondary: '#000000',
		textFooter: '#000000',
		textDisabled: '#8FA4AE',

		backgroundMain: '#FFFFFF',
		backgroundSecondary: '#F1F2F3',
		backgroundCanvas: '#FFFFFF',
		backgroundFooter: '#FFFFFF',
		background3d: '#F1F2F3',
		backgroundPreview: '#DFDFE0',

		borders: '#DBE2E6',

		iconMain: '#1A1C1D',
		iconSecondary: '#1A1C1D',
		iconSecondaryState: '#0073D3',
		iconDisabled: '#BABEC3',

		error: '#DE1C22',
		errorText: '#9F1B1F',
		errorLight: '#FFF4F4',
		success: '#086343',

		scrollAlternative: '#8fa4ae',
	},
	tools: {
		variants: {
			defaultText: 'Variants',
			defaultLongText: 'Switch Variants',
			defaultIconKey: 'switch-variant',
			key: 'variants',
			visible: true,
		},
		designElements: {
			defaultText: 'Design',
			defaultLongText: 'Design',
			defaultIconKey: 'design-elements',
			key: 'designElements',
			position: 1,
		},
		uploadImage: {
			defaultText: 'Uploads',
			defaultLongText: 'Uploads',
			defaultIconKey: 'upload-image',
			key: 'uploadImage',
			position: 2,
		},
		cliparts: {
			defaultText: 'Images gallery',
			defaultLongText: 'Images gallery',
			defaultIconKey: 'cliparts',
			key: 'cliparts',
			position: 3,
		},
		text: {
			defaultText: 'Text',
			defaultLongText: 'Text',
			defaultIconKey: 'text',
			key: 'text',
			position: 4,
		},
		textArt: {
			defaultText: 'TextArt',
			defaultLongText: 'TextArt',
			defaultIconKey: 'textart',
			key: 'textArt',
			position: 5,
		},
		background: {
			defaultText: 'Fill',
			defaultLongText: 'Fill Background',
			defaultIconKey: 'background',
			key: 'background',
			position: 6,
		},
		templates: {
			defaultText: 'Templates',
			defaultLongText: 'Pre-Designed Templates',
			defaultIconKey: 'templates',
			key: 'templates',
			position: 7,
		},
		collages: {
			defaultText: 'Collages',
			defaultLongText: 'Collages',
			defaultIconKey: 'collages',
			key: 'collages',
			position: 8,
			visible: true,
		},
		shapes: {
			defaultText: 'Shapes',
			defaultLongText: 'Shapes',
			defaultIconKey: 'shapes',
			key: 'shapes',
			position: 9,
			visible: true,
		},
		palette: {
			key: 'palette',
			visible: true,
		},
		printingMethods: {
			defaultText: 'Printing Methods',
			defaultLongText: 'Printing Methods',
			defaultIconKey: 'printing-methods',
			key: 'printingMethods',
			visible: true,
		},
		drafts: {
			defaultText: 'My saved designs',
			defaultLongText: 'My saved designs',
			defaultIconKey: 'drafts',
			key: 'drafts',
		},
		cart: {
			defaultText: 'Add to cart',
			defaultLongText: 'Add to cart',
			defaultIconKey: 'cart',
			key: 'cart',
		},
	},
	spacing: {
		horizontalPadderNumber,
		horizontalPadderMobileNumber,
		horizontalPadder: `${horizontalPadderNumber}px`,
		horizontalPadderMobile: `${horizontalPadderMobileNumber}px`,
	},
	measures: {
		borderRadius: '4px',
	},
	panels: {
		borderWidthValue: panelsBorderWidth,
		borderBottomWidthValue: panelsBorderBottomWidth,
		borderWidth: `${panelsBorderWidth}px ${panelsBorderWidth}px ${panelsBorderBottomWidth}px ${panelsBorderWidth}px`,
		shadows: '0 0 1px rgba(0, 0, 0, 0.25), 0 2px 1px rgba(0, 0, 0, 0.05)',
	},
	dialogs: {
		backdrop: 'rgba(0, 0, 0, 0.3)',
		shadows: '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 3px rgba(0, 0, 0, 0.1)',
	},
	pricing: {
		showPrice: true,
		showProductQuantity: true,
	},
	zoom: {
		zoomPercentageIncrement: 100,
	},
	preview3d: {
		allowPreview3DExpandOnContainerClick: false,
		showResize3DPreview: false,
		showResize3DHeight: 200,
		showResize3DWidth: 220,
	},
	general: {
		measurementUnit: 'cm',
		showSideDimensions: true,
		showElementsDimensions: true,
		showSelectedElementDimensions: false,
		showAreaDimensions: false,
		isUndoRedoEnabled: true,
	},
	desktopLayout: {
		menuPosition: 'right',
		footerPosition: 'bottom',
	},
};

type ThemeOverrides = {
	fontFamily?: Partial<DefaultTheme['fontFamily']>;
	fonts?: Partial<DefaultTheme['fonts']>;
	colors?: Partial<DefaultTheme['colors']>;
	tools?: Partial<DefaultTheme['tools']>;
	spacing?: Partial<DefaultTheme['spacing']>;
	measures?: Partial<DefaultTheme['measures']>;
	panels?: Partial<DefaultTheme['panels']>;
	dialogs?: Partial<DefaultTheme['dialogs']>;
	pricing?: Partial<DefaultTheme['pricing']>;
	zoom?: Partial<DefaultTheme['zoom']>;
	preview3d?: Partial<DefaultTheme['preview3d']>;
	general?: Partial<DefaultTheme['general']>;
	desktopLayout?: Partial<DefaultTheme['desktopLayout']>;
};

export const createTheme = (overrides: ThemeOverrides): DefaultTheme => {
	const fontFamily = overrides.fontFamily?.styleUrl ? overrides.fontFamily : defaultTheme.fontFamily;
	return {
		...defaultTheme,
		fontFamily: {
			...defaultTheme.fontFamily,
			...fontFamily,
			allTextUppercase: overrides.fontFamily?.allTextUppercase ?? defaultTheme.fontFamily.allTextUppercase,
		},
		fonts: {
			...defaultTheme.fonts,
			...createThemeFont(fontFamily.name ?? defaultTheme.fontFamily.name),
		},
		colors: {
			...defaultTheme.colors,
			...overrides.colors,
		},
		tools: {
			// make sure is a new object
			...merge({}, defaultTheme.tools, overrides.tools),
		},
		spacing: {
			...defaultTheme.spacing,
			...overrides.spacing,
		},
		measures: {
			...defaultTheme.measures,
			...overrides.measures,
		},
		panels: {
			...defaultTheme.panels,
			...overrides.panels,
		},
		dialogs: {
			...defaultTheme.dialogs,
			...overrides.dialogs,
		},
		pricing: {
			...defaultTheme.pricing,
			...overrides.pricing,
		},
		zoom: {
			...defaultTheme.zoom,
			...overrides.zoom,
		},
		preview3d: {
			...defaultTheme.preview3d,
			...overrides.preview3d,
		},
		general: {
			...defaultTheme.general,
			...overrides.general,
		},
		desktopLayout: {
			...merge({}, defaultTheme.desktopLayout, overrides.desktopLayout),
		},
	};
};
